export interface ThemeModePalette {
  primary: string;
  secondary: string;
  textDefault: string;
  textPrimary: string;
  textSecondary: string;
  textTertiary: string;
  textDisabled: string;
  textDanger: string;
  textLight: string;
  bgPage: string;
  bgContainer: string;
  bgElevated: string;
}

export interface ThemeDefinition {
  light: ThemeModePalette;
  dark: ThemeModePalette;
}

export const DEFAULT_THEME: ThemeDefinition = {
  light: {
    primary: '#21524e',
    secondary: '#1a4542',
    textDefault: '#141414',
    textPrimary: '#0f5c56',
    textSecondary: '#666666',
    textTertiary: '#6b7280',
    textDisabled: '#9ca3af',
    textDanger: '#dc2626',
    textLight: '#ffffff',
    bgPage: '#e4e6e9',
    bgContainer: '#f5f6f8',
    bgElevated: '#ffffff',
  },
  dark: {
    primary: '#42d6ca',
    secondary: '#35b5aa',
    textDefault: '#e6e6e6',
    textPrimary: '#40eee0',
    textSecondary: '#aaaaaa',
    textTertiary: '#9ca3af',
    textDisabled: '#6b7280',
    textDanger: '#f87171',
    textLight: '#141414',
    bgPage: '#1a1a1a',
    bgContainer: '#252525',
    bgElevated: '#2e2e2e',
  },
};

function hexWithAlpha(hex: string, alpha: number): string {
  const a = Math.round(alpha * 255)
    .toString(16)
    .padStart(2, '0');
  return `${hex}${a}`;
}

/** Maps editable palette to CSS custom properties used by Tailwind tokens */
export function paletteToCssVars(palette: ThemeModePalette): Record<string, string> {
  return {
    '--bgPrimary': palette.primary,
    '--bgSecondary': palette.secondary,
    '--textDefault': palette.textDefault,
    '--textPrimary': palette.textPrimary,
    '--textSecondary': palette.textSecondary,
    '--textTertiary': palette.textTertiary,
    '--textDisabled': palette.textDisabled,
    '--textDanger': palette.textDanger,
    '--textLight': palette.textLight,
    '--textPrimary-80': palette.textPrimary,
    '--iconColorActive': palette.primary,
    '--BgPrimaryLight': hexWithAlpha(palette.primary, 0.1),
    '--bgLight': palette.bgPage,
    '--bgDefault': palette.bgPage,
    '--bgLayout': palette.bgPage,
    '--bgContainer': palette.bgContainer,
    '--bgBase': palette.bgContainer,
    '--bgElevated': palette.bgElevated,
    '--bgCard': palette.bgElevated,
  };
}

export function themeToCssVarStyle(theme: ThemeDefinition, mode: 'light' | 'dark'): Record<string, string> {
  const palette = mode === 'dark' ? theme.dark : theme.light;
  return paletteToCssVars(palette);
}

/** Relative luminance for WCAG contrast check */
function luminance(hex: string): number {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const transform = (c: number) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  return 0.2126 * transform(r) + 0.7152 * transform(g) + 0.0722 * transform(b);
}

export function contrastRatio(fg: string, bg: string): number {
  const l1 = luminance(fg);
  const l2 = luminance(bg);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

export function hasLowContrast(textColor: string, bgColor: string): boolean {
  return contrastRatio(textColor, bgColor) < 4.5;
}
